import { useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeCardElement } from '@stripe/stripe-js';
import { useSegment } from '@repo/poly-hooks/segment';
import { useSentry } from '@repo/shared-kit/sentry/context';

import { type PaymentIntent, type PaymentIntentAdapter } from './types';

interface UseStripeCardPaymentParams {
  pricePlanId: string;
  paymentIntentAdapter: PaymentIntentAdapter;
  onPaymentSuccess: () => void | Promise<void>;
  /**
   * Receives either a Stripe `StripeError` (no HTTP `status`) or the error
   * thrown by `PaymentIntentAdapter` (app-defined, typically has `.status`).
   */
  onPaymentError: (error: unknown) => void | Promise<void>;
}

interface UseStripeCardPaymentResult {
  submit: () => Promise<void>;
  isSubmitting: boolean;
}

export function useStripeCardPayment({
  pricePlanId,
  paymentIntentAdapter,
  onPaymentSuccess,
  onPaymentError,
}: UseStripeCardPaymentParams): UseStripeCardPaymentResult {
  const stripe = useStripe();
  const elements = useElements();
  const segment = useSegment();
  const sentry = useSentry();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    if (isSubmitting) return;

    if (!stripe || !elements) {
      segment.track('stripe_submit_error', { reason: 'stripe_not_loaded' });
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      segment.track('stripe_submit_error', {
        reason: 'card_element_not_mounted',
      });
      sentry.captureException(new Error('Stripe card element not mounted'));
      return;
    }

    setIsSubmitting(true);

    try {
      await processStripeCardPayment({ cardElement, paymentIntentAdapter, pricePlanId, stripe });
    } catch (error) {
      await onPaymentError(error);
      return;
    } finally {
      setIsSubmitting(false);
    }

    try {
      await onPaymentSuccess();
    } catch (error) {
      segment.track('stripe_submit_error', { reason: 'success_callback_failed' });
      sentry.captureException(error);
    }
  };

  return { submit, isSubmitting };
}

async function processStripeCardPayment({
  cardElement,
  paymentIntentAdapter,
  pricePlanId,
  stripe,
}: {
  cardElement: StripeCardElement;
  paymentIntentAdapter: PaymentIntentAdapter;
  pricePlanId: string;
  // we type a subset of the Stripe object to avoid having to mock the entire Stripe object in tests
  stripe: Pick<Stripe, 'createPaymentMethod' | 'handleCardAction'>;
}): Promise<void> {
  const { error, paymentMethod } = await stripe.createPaymentMethod({
    type: 'card',
    card: cardElement,
  });

  if (error) {
    throw error;
  }

  const paymentIntent = await paymentIntentAdapter.createPaymentIntent({
    paymentMethodId: paymentMethod.id,
    pricePlanId,
  });

  await confirmPaymentIntent(stripe, paymentIntent, paymentIntentAdapter);
}

async function confirmPaymentIntent(
  stripe: Pick<Stripe, 'handleCardAction'>,
  paymentIntent: PaymentIntent,
  paymentIntentAdapter: PaymentIntentAdapter
): Promise<void> {
  if (!paymentIntent.requiresAction) return;

  const { error } = await stripe.handleCardAction(paymentIntent.clientSecret);
  if (error) throw error;

  const nextPaymentIntent = await paymentIntentAdapter.confirmPaymentIntent(paymentIntent.id);
  await confirmPaymentIntent(stripe, nextPaymentIntent, paymentIntentAdapter);
}
